/**
 * Lagrar kopplingar mellan ett centralspec-dokument (apps/panels) och ett
 * schemaeditor-projekt (apps/web). Varje koppling sparar den senaste
 * ögonblicksbilden av apparaterna — det är inte en live-synk utan en
 * kopierad lista som användaren kan uppdatera manuellt genom att exportera
 * på nytt från schemaeditorn.
 *
 * Fas F är medvetet enkel: import sker via JSON-fil som användaren hämtat
 * från schemaeditorn. Tvåvägs-synk via server kommer senare.
 */

const STORAGE_PREFIX = "electro-planner-panels:linked:";

export type LinkedDevice = {
  designation: string;
  deviceKind?: string;
  symbolId?: string;
  label?: string;
  usage?: string;
};

export type LinkedProject = {
  version: 1;
  source: "electro-planner-editor";
  /** När importen skedde — inte samma som editor-export-tidsstämpel. */
  importedAt: string;
  /** Editor-exportens tidsstämpel. */
  exportedAt: string;
  documentId?: string;
  projectTitle?: string;
  devices: LinkedDevice[];
};

export function readLinkedProject(documentId: string): LinkedProject | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + documentId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LinkedProject;
    if (parsed.version !== 1 || !Array.isArray(parsed.devices)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeLinkedProject(
  documentId: string,
  project: LinkedProject,
): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + documentId, JSON.stringify(project));
  } catch {
    // localStorage kan vara full eller blockerad. Ignorera tyst — nästa
    // export kan försöka igen och felmeddelande visas via import-dialogen.
  }
}

export function clearLinkedProject(documentId: string): void {
  localStorage.removeItem(STORAGE_PREFIX + documentId);
}

/**
 * Validerar ett råt JSON-paket och returnerar ett Linked-objekt om det är
 * ett giltigt Centralspec-export-paket. null annars.
 */
export function parseLinkedProjectJson(text: string): LinkedProject | null {
  try {
    const raw = JSON.parse(text) as Record<string, unknown>;
    if (raw.version !== 1) return null;
    if (raw.source !== "electro-planner-editor") return null;
    const devices = Array.isArray(raw.devices) ? raw.devices : [];
    const cleanedDevices: LinkedDevice[] = [];
    for (const d of devices) {
      if (typeof d !== "object" || d === null) continue;
      const dd = d as Record<string, unknown>;
      if (typeof dd["designation"] !== "string") continue;
      cleanedDevices.push({
        designation: dd["designation"] as string,
        deviceKind: typeof dd["deviceKind"] === "string" ? (dd["deviceKind"] as string) : undefined,
        symbolId: typeof dd["symbolId"] === "string" ? (dd["symbolId"] as string) : undefined,
        label: typeof dd["label"] === "string" ? (dd["label"] as string) : undefined,
        usage: typeof dd["usage"] === "string" ? (dd["usage"] as string) : undefined,
      });
    }
    return {
      version: 1,
      source: "electro-planner-editor",
      importedAt: new Date().toISOString(),
      exportedAt: typeof raw["exportedAt"] === "string" ? (raw["exportedAt"] as string) : new Date().toISOString(),
      documentId: typeof raw["documentId"] === "string" ? (raw["documentId"] as string) : undefined,
      projectTitle: typeof raw["projectTitle"] === "string" ? (raw["projectTitle"] as string) : undefined,
      devices: cleanedDevices,
    };
  } catch {
    return null;
  }
}
